import { useQueryClient } from '@tanstack/react-query';
import { type Dispatch, type ReactNode, type SetStateAction } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { type Event } from 'hb-react/shared/apps/Events/types';

interface EventContextType {
  config: Record<string, unknown>;
  event: Event;
  eventDetailsPageLink: string;
  eventRegistrationPageLink: string;
  updateEvent: Dispatch<SetStateAction<Event>>;
}

const EventContext = createContext({} as EventContextType);

export default EventContext;

export const useEventContext = () => useContext<EventContextType>(EventContext);

export const withEventContext = (Component) => (props) => {
  const contextValue = useEventContext();

  return <Component {...props} {...contextValue} />;
};

const getEventDetailsPageLink = (
  isLoggedIn,
  eventId,
  topicId,
  isWhitelabelGroup,
) => {
  if (topicId && !isWhitelabelGroup) {
    return Routes.topic_event_path(topicId, eventId);
  }

  if (!isLoggedIn) {
    return Routes.network_event_path(eventId);
  }

  return Routes.network_events_event_path(eventId);
};

const getEventRegistrationPageLink = (
  isLoggedIn,
  eventId,
  topicId,
  isWhitelabelGroup,
) => {
  if (topicId && !isWhitelabelGroup) {
    return Routes.topic_event_registration_path(topicId, eventId);
  }

  if (!isLoggedIn) {
    return Routes.network_event_registration_path(eventId);
  }

  return Routes.network_events_event_registration_path(eventId);
};

export const EventProvider = ({
  children,
  event,
  config = {},
}: {
  children: ReactNode;
  config?: Record<string, unknown>;
  event: Event;
}) => {
  const { currentUser } = useDefaultAppContexts();
  const topicContext = useTopicContext();
  const queryClient = useQueryClient();

  const [eventState, setEventState] = useState(event);

  const updateEvent = useCallback(
    (newEvent) => {
      setEventState(newEvent);
      queryClient.invalidateQueries({
        queryKey: ['event', newEvent.id],
      });
    },
    [queryClient],
  );

  useEffect(() => setEventState(event), [event]);

  const isLoggedIn = !!currentUser?.id;

  const providerState = useMemo(
    () => ({
      event: eventState,
      updateEvent,
      eventDetailsPageLink: getEventDetailsPageLink(
        isLoggedIn,
        event.id,
        topicContext?.topicId,
        !!topicContext?.whitelabelURL,
      ),
      eventRegistrationPageLink: getEventRegistrationPageLink(
        isLoggedIn,
        event.id,
        topicContext?.topicId,
        !!topicContext?.whitelabelURL,
      ),
      config,
    }),
    [eventState, isLoggedIn, topicContext, event.id, config],
  );

  return (
    <EventContext.Provider value={providerState}>
      {children}
    </EventContext.Provider>
  );
};
